import React, { useEffect, useState } from "react";
import { PulseLoader } from "react-spinners";
import axiosInstance from "../api/axiosInstance";
import { endpoints } from "../services/endpoints";
import { showErrorToast, showSuccessToast } from "../helpers/toast";

const ImageManagement = () => {
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categoryLoading, setCategoryLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);
        const response = await axiosInstance.get(endpoints.CategoryList);
        setCategories(response.data?.data || []);
      } catch (error) {
        showErrorToast(error?.response?.data?.message || "Failed to load quiz categories");
      } finally {
        setCategoryLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    setImage(file || null);
    setImageUrl("");
    setPreview(file ? URL.createObjectURL(file) : "");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!image) {
      showErrorToast("Please select an image");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("image", image);

      const response = await axiosInstance.post(endpoints.uploadImage, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const uploadedUrl = response.data?.data?.imageUrl;
      setImageUrl(uploadedUrl || "");
      showSuccessToast(response.data?.message || "Image uploaded successfully");
    } catch (error) {
      showErrorToast(error?.response?.data?.message || "Failed to upload image");
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (value, successMessage) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      showSuccessToast(successMessage);
    } catch (error) {
      showErrorToast("Unable to copy");
    }
  };

  return (
    <div className="content-wrapper">
      <section className="main-section spacer-y">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-8">
              <div className="common_sub_heading_wrapper mb-3">
                <h2>Quiz Support Tools</h2>
              </div>

              <div className="bg-white border rounded p-3 mb-3">
                <h6>Quiz Category ID</h6>
                <div className="row g-2 align-items-end">
                  <div className="col-12 col-md-6">
                    <select
                      className="form-select"
                      value={selectedCategoryId}
                      onChange={(event) => setSelectedCategoryId(event.target.value)}
                      disabled={categoryLoading}
                    >
                      <option value="">Select Quiz Category</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="input-group">
                      <input className="form-control" value={selectedCategoryId} readOnly />
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => copyText(selectedCategoryId, "Category ID copied")}
                        disabled={!selectedCategoryId}
                      >
                        Copy ID
                      </button>
                    </div>
                  </div>
                </div>
                {categoryLoading && (
                  <div className="mt-3">
                    <PulseLoader size={10} color="#49a496" />
                  </div>
                )}
              </div>

              <form className="bg-white border rounded p-3" onSubmit={handleSubmit}>
                <h6>Upload Image</h6>
                <div className="row g-3">
                  <div className="col-12">
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </div>

                  {preview && (
                    <div className="col-12 text-center">
                      <img
                        src={preview}
                        alt="Preview"
                        style={{ maxWidth: "240px", maxHeight: "180px", borderRadius: "6px" }}
                      />
                    </div>
                  )}

                  <div className="col-12 d-flex gap-2 align-items-center">
                    <button type="submit" className="btn btn-success" disabled={loading}>
                      Upload
                    </button>
                    {loading && <PulseLoader size={10} color="#49a496" />}
                  </div>
                </div>

                {imageUrl && (
                  <div className="mt-4">
                    <h6>S3 Image URL</h6>
                    <div className="input-group">
                      <input className="form-control" value={imageUrl} readOnly />
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => copyText(imageUrl, "Image URL copied")}
                      >
                        Copy
                      </button>
                    </div>
                    <div className="mt-3 text-center">
                      <img
                        src={imageUrl}
                        alt="Uploaded"
                        style={{ maxWidth: "240px", maxHeight: "180px", borderRadius: "6px" }}
                      />
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImageManagement;
